import { useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useRail } from '../context/RailContext';

function ItemBlock({ className, content }) {
    if (!content) return null;
    return (
        <div className={className}>
            {typeof content === 'string' ? <p>{content}</p> : content}
        </div>
    );
}

function getDates(item) {
    if (Array.isArray(item.dates)) return item.dates;
    return item.date ? [item.date] : [];
}

export default function ExpandableRailList({ items, header }) {
    const { register, unregister, openIndex, selectItem } = useRail();
    const shouldReduceMotion = useReducedMotion();
    const activeItem = openIndex !== null ? items[openIndex] : null;

    useEffect(() => {
        register(items);
        return () => unregister();
    }, [items, register, unregister]);

    return (
        <AnimatePresence mode="wait">
            {openIndex === null ? (
                /* ── State 1: large right-side list (like /contact) ── */
                <motion.div
                    key="rail-list"
                    className="page-right-shell"
                    initial={shouldReduceMotion ? false : { opacity: 0 }}
                    animate={{
                        opacity: 1,
                        transition: shouldReduceMotion ? { duration: 0 } : {
                            duration: 0.3,
                            ease: [0.22, 1, 0.36, 1],
                        },
                    }}
                    exit={shouldReduceMotion ? undefined : {
                        opacity: 0,
                        transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
                    }}
                >
                    <div className="page-right">
                        {header && <p className="page-right__header">{header}</p>}
                        <ul>
                            {items.map((item, index) => (
                                <li key={item.title} onClick={() => selectItem(index)}>
                                    <span className="heading">{item.italicTitle ? <em>{item.title}</em> : item.title}</span>
                                    {getDates(item).map((date) => (
                                        <span key={date} className="date">{date}</span>
                                    ))}
                                </li>
                            ))}
                        </ul>
                    </div>
                </motion.div>
            ) : (
                /* ── State 2: active entry content (full width) ── */
                <motion.article
                    key={`active-${activeItem.title}`}
                    className="page-active-entry"
                    initial={shouldReduceMotion ? false : { opacity: 0 }}
                    animate={{
                        opacity: 1,
                        transition: shouldReduceMotion ? { duration: 0 } : {
                            duration: 0.25,
                            ease: [0.22, 1, 0.36, 1],
                        },
                    }}
                    exit={shouldReduceMotion ? undefined : {
                        opacity: 0,
                        transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
                    }}
                >
                    <button
                        type="button"
                        className="page-active-entry__header"
                        onClick={() => selectItem(openIndex)}
                    >
                        <span className="heading">{activeItem.italicTitle ? <em>{activeItem.title}</em> : activeItem.title}</span>
                        {getDates(activeItem).map((date) => (
                            <span key={date} className="date">{date}</span>
                        ))}
                    </button>

                    <ItemBlock
                        className="page-active-entry__subtitle"
                        content={activeItem.subtitle}
                    />
                    <ItemBlock
                        className="page-active-entry__details"
                        content={activeItem.blurb}
                    />
                </motion.article>
            )}
        </AnimatePresence>
    );
}
